/* eslint-disable react/prop-types, no-unused-vars */
import { create } from 'zustand';
import axios from '../api/axios';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.DEV ? 'http://localhost:3000/api' : '/api';

export const useRestaurantStore = create((set, get) => ({
  tables: [],
  events: [],
  products: [],
  ingredients: [],
  waiters: [],
  kitchenOrders: [],
  activeOrder: null,
  isLoading: false,
  error: null,

  fetchWaiters: async () => {
    try {
      const res = await axios.get(`/restaurant/waiters`);
      set({ waiters: res.data });
    } catch (error) {
      set({ error: error.response?.data?.error || error.message });
    }
  },

  fetchTables: async () => {
    try {
      const res = await axios.get(`/restaurant/tables?_t=${Date.now()}`);
      set({ tables: res.data });
    } catch (error) {
      set({ error: error.response?.data?.error || error.message });
    }
  },

  fetchEvents: async () => {
    try {
      const res = await axios.get(`/events`);
      set({ events: res.data.filter(e => e.status === 'ACTIVE') });
    } catch (error) {
      console.error(error);
    }
  },

  fetchProducts: async () => {
    try {
      const res = await axios.get(`/restaurant/products`);
      set({ products: res.data });
    } catch (error) {
      set({ error: error.response?.data?.error || error.message });
    }
  },

  fetchIngredients: async () => {
    try {
      const res = await axios.get(`/restaurant/ingredients`);
      set({ ingredients: res.data });
    } catch (error) {
      set({ error: error.response?.data?.error || error.message });
    }
  },

  fetchKitchenOrders: async () => {
    try {
      const res = await axios.get(`/restaurant/orders/kitchen`);
      set({ kitchenOrders: res.data });
    } catch (error) {
      set({ error: error.response?.data?.error || error.message });
    }
  },

  fetchActiveOrders: async (tableId) => {
    try {
      set({ isLoading: true });
      const isEvent = tableId && String(tableId).startsWith('evt_');
      const isStay = tableId && String(tableId).startsWith('stay_');
      
      let url = `/restaurant/orders/table/${tableId}`;
      if (isEvent) {
        url = `/events/${tableId.replace('evt_', '')}/active-order`;
      }
      // isStay already uses the same /table/ tableId logic in the backend
      // because we updated getOrderByTable to handle stay_...
      const res = await axios.get(`${url}?_t=${Date.now()}`);
      
      // Normalize to array
      let orders = [];
      if (Array.isArray(res.data)) {
        orders = res.data;
      } else if (res.data) {
        orders = [res.data];
      }
      
      set({ activeOrders: orders, isLoading: false });
    } catch (error) {
      set({ error: error.message, activeOrders: [], isLoading: false });
    }
  },

  updateOrderStatus: async (orderId, status) => {
    try {
      await axios.put(`/restaurant/orders/${orderId}/status`, { status });
      // Refresh kitchen orders
      get().fetchKitchenOrders();
    } catch (error) {
      set({ error: error.response?.data?.error || error.message });
    }
  },

  markItemDelivered: async (itemId) => {
    const previousKitchenOrders = get().kitchenOrders;
    const previousActiveOrders = get().activeOrders;

    // Optimistic UI Update (Instant visual feedback)
    set({
      kitchenOrders: get().kitchenOrders.map(order => ({
        ...order,
        items: order.items.map(item => item.id === itemId ? { ...item, status: 'DELIVERED' } : item)
      })),
      activeOrders: get().activeOrders?.map(order => ({
        ...order,
        items: order.items.map(item => item.id === itemId ? { ...item, status: 'DELIVERED' } : item)
      }))
    });

    try {
      await axios.put(`/restaurant/order-items/${itemId}/deliver`);
      // We don't fetch again to avoid flicker, since we optimistically updated. 
      // Background sync or sockets will handle other clients.
      toast.success('Ítem marcado como entregado');
    } catch (error) {
      // Revert if failed
      set({ kitchenOrders: previousKitchenOrders, activeOrders: previousActiveOrders });
      toast.error('Error al actualizar ítem. Se revirtió el cambio.');
    }
  },

  deliverPartialItem: async (itemId, quantityToDeliver) => {
    try {
      await axios.put(`/restaurant/order-items/${itemId}/deliver-partial`, { quantityToDeliver });
      get().fetchKitchenOrders();
      if (get().activeOrder || get().activeOrders?.length > 0) {
        get().fetchActiveOrders(get().activeOrders?.[0]?.tableId);
      }
      toast.success(`Entregados ${quantityToDeliver} ítem(s)`);
    } catch (error) {
      toast.error('Error al entregar ítems parcialmente');
    }
  },

  editOrderItem: async (orderId, itemId, payload, token) => {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.put(`/restaurant/orders/${orderId}/items/${itemId}`, payload, config);
      get().fetchActiveOrders(get().activeOrders?.[0]?.tableId);
      toast.success('Comanda actualizada (Auditoría guardada)');
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al editar ítem';
      toast.error(errorMsg);
      return false;
    }
  },

  verifyPin: async (pin) => {
    try {
      const response = await axios.post(`/restaurant/auth/verify-pin`, { pin });
      return response.data;
    } catch (error) {
      console.error('Error verifying PIN', error);
      throw error;
    }
  },

  applyDiscount: async (orderId, discountAmount, reason, authUserId, token) => {
    try {
      const response = await axios.post(`/restaurant/orders/${orderId}/discount`, { discountAmount, reason, authUserId }, { headers: { Authorization: `Bearer ${token}` } });
      set({ activeOrder: response.data });
      get().fetchTables();
    } catch (error) {
      console.error('Error applying discount', error);
      throw error;
    }
  },

  applyCourtesy: async (orderId, reason, authUserId, token) => {
    try {
      const response = await axios.post(`/restaurant/orders/${orderId}/courtesy`, { reason, authUserId }, { headers: { Authorization: `Bearer ${token}` } });
      set({ activeOrder: response.data });
      get().fetchTables();
    } catch (error) {
      console.error('Error applying courtesy', error);
      throw error;
    }
  },

  markItemCourtesy: async (orderId, itemId, reason, userId, token) => {
    try {
      const response = await axios.post(`/restaurant/orders/${orderId}/items/${itemId}/courtesy`, { reason, userId }, { headers: { Authorization: `Bearer ${token}` } });
      set({ activeOrder: response.data });
      get().fetchTables();
    } catch (error) {
      console.error('Error marking item courtesy', error);
      throw error;
    }
  },

  overrideItemPrice: async (orderId, itemId, newPrice, reason, authUserId, token) => {
    try {
      const response = await axios.put(`/restaurant/orders/${orderId}/items/${itemId}/price`, { newPrice, reason, authUserId }, { headers: { Authorization: `Bearer ${token}` } });
      set({ activeOrder: response.data });
      get().fetchTables();
    } catch (error) {
      console.error('Error overriding price', error);
      throw error;
    }
  },

  submitOrder: async (tableId, items, userId = 'user-1', clientName = '', guestCount = 1, appendToOrderId = null) => {
    try {
      set({ isLoading: true });
      const isEvent = tableId && String(tableId).startsWith('evt_');
      const isStay = tableId && String(tableId).startsWith('stay_');
      const actualTableId = (isEvent || isStay) ? null : tableId;
      const actualEventId = isEvent ? tableId.replace('evt_', '') : null;
      const actualStayId = isStay ? tableId.replace('stay_', '') : null;

      if (appendToOrderId) {
        // Add to existing order explicitly
        await axios.post(`/restaurant/orders/${appendToOrderId}/items`, { items });
      } else {
        // Create new order
        await axios.post(`/restaurant/orders`, {
          tableId: actualTableId,
          eventId: actualEventId,
          stayId: actualStayId,
          items,
          userId,
          clientName,
          guestCount
        });
      }
      
      get().fetchTables(); // Refresh tables status
      get().fetchActiveOrders(tableId); // Refresh active orders
      set({ isLoading: false });
      toast.success('Pedido enviado a cocina');
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || error.response?.data?.error || error.message;
      set({ error: errorMsg, isLoading: false });
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error al enviar pedido');
      return false;
    }
  },
  
  payOrder: async (orderId, paymentMethod = 'CASH', stayId = null, eventId = null, tip = 0, splitAmounts = null) => {
    try {
      set({ isLoading: true });
      await axios.post(`/restaurant/orders/${orderId}/pay`, { paymentMethod, stayId, eventId, tip, splitAmounts });
      get().fetchTables(); // Refresh tables
      set({ activeOrder: null, isLoading: false });
      toast.success(paymentMethod === 'ROOM_CHARGE' ? 'Cargo a habitación registrado' : 'Pago procesado correctamente');
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || error.response?.data?.error || error.message;
      set({ error: errorMsg, isLoading: false });
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error al procesar pago');
    }
  },

  changeOrderTable: async (orderId, newTableId) => {
    try {
      set({ isLoading: true });
      await axios.put(`/restaurant/orders/${orderId}/change-table`, { newTableId });
      get().fetchTables();
      set({ isLoading: false });
      toast.success('Mesa cambiada correctamente');
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || error.response?.data?.error || error.message;
      set({ error: errorMsg, isLoading: false });
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error al cambiar de mesa');
      return false;
    }
  }
}));
